import * as React from 'react';
import { FC, useCallback } from 'react';

import { VStack } from '@rbilabs/universal-components';
import LottieView from 'lottie-react-native';

import { useEnableUnifiedMenu } from 'hooks/use-enable-unified-menu';
import { actions, selectors, useAppDispatch, useAppSelector } from 'state/global-state';
import { RewardRedemptionMethod } from 'state/loyalty/hooks/types';
import { useGetAvailableRewards } from 'state/loyalty/hooks/use-get-available-rewards';
import { useLoyaltyUser } from 'state/loyalty/hooks/use-loyalty-user';
import { isCartEntryPicker } from 'utils/cart/is-cart-type';
import { HapticsNotificationType, hapticNotification } from 'utils/haptic';

import { useRedeemReward } from '../hooks/use-redeem-reward-state';

import { ConfettiAnimation } from './confetti-animation';
import { RedeemRewardView } from './redeem-reward.view';
import { IRedeemRewardProps, RewardActions } from './types';

/**
 *
 * RedeemReward card displays below cart items that are eligible for reward redemptions
 * Users can redeem rewards using points from their loyalty balance in order to receive discounts on their order
 *
 */
export const RedeemReward: FC<React.PropsWithChildren<IRedeemRewardProps>> = ({
  isRewardApplied,
  item,
  reward,
  disabled = false,
}) => {
  const { getAvailableRewardFromCartEntry } = useGetAvailableRewards();
  const { rewardRedeemable, incentiveNotInMenu, redeemRewardLoading } = useRedeemReward(item);
  const dispatch = useAppDispatch();
  const appliedLoyaltyRewards = useAppSelector(selectors.loyalty.selectAppliedLoyaltyRewards);
  const { loyaltyUser } = useLoyaltyUser();
  const { pointCost, name } = reward;
  const confettiAnimationRef = React.useRef<LottieView>(null);

  const isUnifiedMenuEnabled = useEnableUnifiedMenu();

  const handleRewardClick = useCallback(
    (action: RewardActions) => async () => {
      const { applyReward, setIsPricingRewardApplication, unApplyReward } = actions.loyalty;
      dispatch(setIsPricingRewardApplication(true));

      const isPickerEntry = isCartEntryPicker(item.url);

      // If the cart entry is a picker that results in a selected product being an item,
      // and this item can be redeemed as a reward,
      // We should switch the entry url to be either item or combo.
      // This way the One Menu PDP can handle this specific picker reward edit.
      const cartEntry =
        isUnifiedMenuEnabled && isPickerEntry
          ? { ...item, url: `/menu/${item.type.toLowerCase()}-${item._id}` }
          : item;

      const cartEntryReward = getAvailableRewardFromCartEntry(cartEntry);

      if (!cartEntryReward) {
        return;
      }

      if (action === RewardActions.ADD) {
        hapticNotification({ type: HapticsNotificationType.SUCCESS });
        confettiAnimationRef.current?.reset();
        confettiAnimationRef.current?.play();
      }

      const handleAction = {
        [RewardActions.ADD]: applyReward,
        [RewardActions.REMOVE]: unApplyReward,
      }[action];

      dispatch(
        handleAction({
          cartId: cartEntry.cartId,
          rewardBenefitId: cartEntryReward.rewardBenefitId,
          loyaltyUser,
          redemptionMethod: RewardRedemptionMethod.MOBILE,
        })
      );
      hapticNotification({ type: HapticsNotificationType.SUCCESS });
    },
    [dispatch, getAvailableRewardFromCartEntry, isUnifiedMenuEnabled, item, loyaltyUser]
  );

  const numberOfTimesRewardApplied = appliedLoyaltyRewards[item.cartId]?.timesApplied;

  const pointsApplied = (numberOfTimesRewardApplied || 0) * pointCost;

  if (pointCost === 0) {
    return null;
  }

  return (
    <VStack>
      <RedeemRewardView
        handleRewardClick={handleRewardClick}
        isRewardApplied={isRewardApplied}
        points={pointCost}
        rewardTitle={name}
        disableApplyButton={!rewardRedeemable || disabled}
        pointsApplied={pointsApplied}
        itemQuantity={item.quantity}
        incentiveNotInMenu={incentiveNotInMenu}
        redeemRewardLoading={redeemRewardLoading}
      />
      <ConfettiAnimation animationRef={confettiAnimationRef} />
    </VStack>
  );
};
